<script setup lang="ts" generic="TItem extends VSegmentedControlItem">
import type { VSegmentedControlItem } from "./VSegmentedControl.types";
import { computed, ref } from "vue";
import { useSlidingIndicator } from "../../composables/SlidingIndicator/useSlidingIndicator";
import VIcon from "../Icon/VIcon.vue";
import VTag from "../Tag/VTag.vue";
import { indicatorCva, indicatorSurfaceCva, optionCva, trackCva } from "./VSegmentedControl.cva";

const {
  items,
  disabled = false,
  columns = undefined,
  compact = false,
} = defineProps<{
  items: TItem[]
  disabled?: boolean
  columns?: 2 | 3 | 4
  compact?: boolean
}>();

const modelValue = defineModel<TItem["value"] | undefined>({ default: undefined });

const trackEl = ref<HTMLElement | null>(null);
const layout = computed(() => (columns ? "grid" : "wrap"));

// Wide options would bounce the indicator through the track's gutter and into its rounded edge;
// contained, an outer option takes the same bounce as a squash against the gutter instead.
const { bounds: indicator, indicatorStyle, surfaceStyle, setOptionEl } = useSlidingIndicator({
  container: trackEl,
  activeKey: () => modelValue.value,
  enabled: () => !disabled,
  contain: true,
  watch: () => [items, columns, compact],
});

const trackClass = computed(() => trackCva({
  layout: layout.value,
  columns,
  compact,
  disabled,
}));

function isSelected(item: TItem): boolean {
  return modelValue.value === item.value;
}

function isItemDisabled(item: TItem): boolean {
  return disabled || Boolean(item.disabled);
}

function optionClass(item: TItem): string {
  return optionCva({
    selected: isSelected(item),
    indicator: Boolean(indicator.value),
    grow: compact ? "auto" : layout.value === "wrap",
    align: compact ? "center" : "start",
    disabled: isItemDisabled(item),
  });
}

// Radio groups are one tab stop: the selected option, or the first enabled one while nothing is selected.
const tabStopItem = computed(() => {
  const selected = items.find(item => isSelected(item) && !isItemDisabled(item));
  return selected ?? items.find(item => !isItemDisabled(item));
});

function onKeydown(event: KeyboardEvent) {
  let step: 1 | -1;
  switch (event.key) {
    case "ArrowRight":
    case "ArrowDown":
      step = 1;
      break;
    case "ArrowLeft":
    case "ArrowUp":
      step = -1;
      break;
    default:
      return;
  }

  const radios = Array.from(trackEl.value?.querySelectorAll<HTMLButtonElement>("[role=\"radio\"]:not(:disabled)") ?? []);
  const current = radios.findIndex(radio => radio === event.target);
  if (current === -1) {
    return;
  }

  event.preventDefault();
  const next = radios[(current + step + radios.length) % radios.length];
  next.focus();
  next.click();
}

function select(item: TItem) {
  if (isItemDisabled(item) || isSelected(item)) {
    return;
  }

  modelValue.value = item.value;
}
</script>

<template>
  <div
    ref="trackEl"
    role="radiogroup"
    :aria-disabled="disabled || undefined"
    :class="trackClass"
    @keydown="onKeydown"
  >
    <span
      v-if="indicator"
      aria-hidden="true"
      :class="indicatorCva()"
      :style="indicatorStyle"
    >
      <span
        :class="indicatorSurfaceCva()"
        :style="surfaceStyle"
      />
    </span>

    <button
      v-for="item in items"
      :key="String(item.value)"
      :ref="(el) => setOptionEl(item.value, el)"
      type="button"
      role="radio"
      :aria-checked="isSelected(item)"
      :tabindex="tabStopItem === item ? 0 : -1"
      :disabled="isItemDisabled(item)"
      :class="optionClass(item)"
      @click="select(item)"
    >
      <slot name="item" :item="item" :selected="isSelected(item)">
        <span class="flex items-center gap-1.5 text-sm font-semibold leading-none">
          <VIcon
            v-if="item.icon"
            :name="item.icon"
            class="text-xs leading-none"
            aria-hidden="true"
          />
          {{ item.label }}
          <VTag
            v-if="item.badge"
            :text="item.badge"
            variant="is-success"
            size="extra-small"
          />
        </span>
        <span
          v-if="item.description"
          class="text-xs leading-snug text-gray-500 tabular-nums dark:text-gray-400"
        >
          {{ item.description }}
        </span>
      </slot>
    </button>
  </div>
</template>
